import {
  Button,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSelecting,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { use_SchoolEventsQuery } from '../../../types/planung-graphql-client-defs';

type SchoolEventTableType = {
  uuid: string;
  name: string;
  groupClassNamesLong: string;
  groupClassNames: string;
  subjectName: string;
  subjectShortName: string;
  teachers: string;
  teachersLong: string;
  start: string;
  end: string;
  readonly: boolean;
  recurrenceUuid: string | null;
  recurring: boolean;
};

type SchoolEventTableProps = {
  loading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDuplicate: (uuid: string) => void;
  onDelete: (uuids: string[]) => Promise<void>;
  onDeleteRecurring: (recurrenceUuid: string | null) => Promise<void>;
};

export const SchoolEventTable = observer(
  ({ loading, onAdd, onEdit, onDuplicate, onDelete, onDeleteRecurring }: SchoolEventTableProps) => {
    const { t } = useTranslation();
    const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
    const schoolEventContext = useMemorizedCacheTag('SCHOOL_EVENT');
    const { pimAuthClaims } = useAuthClaims();
    const organizationUuid = pimAuthClaims?.getOrganizationUuid();

    const [{ data: eventData }] = use_SchoolEventsQuery({
      variables: {
        organizationUuid: organizationUuid,
        from: currentSchoolYear?.start ?? '',
        to: currentSchoolYear?.end ?? '',
      },
      context: schoolEventContext,
      pause: !currentSchoolYear,
    });

    const { teacherData } = useLoadBasicData({
      pause: !eventData,
    });

    const data: SchoolEventTableType[] = useMemo(() => {
      return (
        eventData?.schoolEvents.map((event) => {
          return {
            uuid: event.uuid,
            name: event.comment ?? '',
            groupClassNames: event.groupClassNames,
            groupClassNamesLong: event.groupClassNamesLong,
            subjectName: event.subject.name ?? '',
            subjectShortName: event.subject.shortName ?? '',
            teachers: event.persons
              .map((t) => {
                const teacher = teacherData?.people.find((teacher) => teacher.uuid === t.uuid);
                return teacher?.displayNameShort ?? t.listName;
              })
              .join(', '),
            teachersLong: event.persons.map((p) => p.listName).join(', '),
            start: dayjs(event.start).format('DD.MM.YYYY HH:mm'),
            end: dayjs(event.start).isSame(event.end, 'day')
              ? dayjs(event.end).format('HH:mm')
              : dayjs(event.end).format('DD.MM.YYYY HH:mm'),
            readonly: event.isClassTrip ?? false,
            recurrenceUuid: event.recurrenceUuid ?? null,
            recurring: !!event.recurrenceUuid,
          };
        }) ?? []
      );
    }, [eventData?.schoolEvents, teacherData?.people]);

    const tooltipFix = { maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden', width: 'fit-content' };

    const columns: TableColumns<SchoolEventTableType>[] = [
      {
        header: t('common.name'),
        accessorKey: 'name',
        id: 'name',
        size: 250,
      },
      {
        header: `${t('classes.title', { count: 2 })}/${t('groups.title', { count: 2 })}`,
        accessorKey: 'groupClassNames',
        id: 'groupClassNames',
        cell: ({ row }) => {
          return (
            <Tooltip triggerStyle={tooltipFix} content={row.original.groupClassNamesLong}>
              {row.original.groupClassNames}
            </Tooltip>
          );
        },
        size: 250,
      },
      {
        header: t('subject.title', { count: 1 }),
        accessorKey: 'subject',
        id: 'subject',
        cell: ({ row }) => {
          return (
            <Tooltip triggerStyle={tooltipFix} content={row.original.subjectName}>
              {row.original.subjectShortName}
            </Tooltip>
          );
        },
        size: 250,
      },
      {
        header: t('persons.title', { count: 2 }),
        accessorKey: 'teachers',
        id: 'teachers',
        cell: ({ row }) => {
          return row.original.teachers === '' ? (
            '---'
          ) : (
            <Tooltip triggerStyle={tooltipFix} content={row.original.teachers}>
              {row.original.teachers}
            </Tooltip>
          );
        },
        size: 250,
      },
      {
        header: t('common.from'),
        accessorKey: 'start',
        id: 'start',
      },
      {
        header: t('common.until'),
        accessorKey: 'end',
        id: 'end',
      },
      {
        header: t('schoolEvents.isRecurring'),
        accessorKey: 'recurring',
        id: 'recurring',
        type: 'boolean',
        size: 100,
      },
    ];
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

    return (
      <Table<SchoolEventTableType>
        isOnWhite={false}
        showBorderRadius
        showShadow
        showActionBar
        pageSize={15}
        showPagination={true}
        canScroll
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        showSelect
        bulkEditDropdownContent={(rows) => {
          return (
            <DropdownMenu
              usePortal={true}
              data={[
                {
                  disabled: rows.filter((r) => !r.original.readonly).length === 0,
                  label: t('common.delete'),
                  type: 'default',
                  color: 'error',
                  onClick: async () => {
                    await onDelete(rows.filter((r) => !r.original.readonly).map((r) => r.original.uuid));
                    onRowSelectionChange({});
                  },
                },
              ]}
            ></DropdownMenu>
          );
        }}
        actionBarSettings={{
          showExpertFilter: true,
          showAddButton: true,
          addButtonText: t('common.add'),
          showSelectedRowsCount: true,
          showBulkEdit: true,
        }}
        onAddClick={() => onAdd()}
        emptyStateSettings={{ title: t('schoolEvents.noEvents') }}
        columns={columns}
        data={data}
        loading={loading}
        lastColWidth='80px'
        lastCol={(row) => {
          return (
            <Dropdown
              disabled={row.original.readonly}
              trigger={<Button disabled={row.original.readonly} icon={<DotsHorizontalIcon />} hierarchy='tertiary' />}
            >
              <DropdownMenu
                data={[
                  {
                    label: t('common.edit'),
                    onClick: () => onEdit(row.original.uuid),
                    disabled: true,
                  },
                  {
                    label: t('common.duplicate'),
                    onClick: () => onDuplicate(row.original.uuid),
                    disabled: true,
                  },
                  {
                    type: 'ruler',
                  },
                  {
                    disabled: !row.original.recurring,
                    label: t('recurrence.delete'),
                    onClick: () => onDeleteRecurring(row.original.recurrenceUuid),
                    color: 'error',
                  },
                  {
                    type: 'ruler',
                  },
                  {
                    label: t('common.delete'),
                    onClick: () => onDelete([row.original.uuid]),
                    color: 'error',
                  },
                ]}
              />
            </Dropdown>
          );
        }}
      />
    );
  },
);
